import { calculators, entities, enums, formatters } from "@fraction/shared";
import { EyeIcon, LandmarkIcon, TrashIcon } from "lucide-react";
import {
  useDeleteBankAccount,
  useDeleteDocument,
  useDeleteIncomeMutation,
  usePreviewDocument,
} from "src/apps/PostFundedDashboard/queries";
import { useConfirmBeforeRun } from "src/hooks/useConfirmBeforeRun";
import PlaidLogo from "src/icons/plaid-logo.svg?react";
import PedestalIllustration from "src/images/pedestal-illustration.png";
import Skeleton from "./Skeleton";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

export function BankAccountList({
  applicationId,
  bankAccounts,
  bankAccountsLoading,
  onClickConnect,
  pendingConnect,
  incomes,
  incomesLoading,
  bankStatements,
  bankStatementsLoading,
}: {
  incomes?: entities.Income[];
  bankAccounts?: entities.BankAccount[];
  bankAccountsLoading?: boolean;
  bankStatementsLoading?: boolean;
  incomesLoading?: boolean;
  onClickConnect?: () => void;
  pendingConnect?: boolean;
  applicationId?: string;
  bankStatements?: entities.UploadedFile[];
}) {
  const loading = bankAccountsLoading || incomesLoading || bankStatementsLoading;
  return (
    <div>
      {!loading && !bankStatements?.length && !bankAccounts?.length && !incomes?.length ? (
        <div className="mt-2 flex w-fit flex-col 2xl:flex-row p-6 border rounded-lg bg-green-50 border-green items-center justify-start 2xl:justify-center h-full gap-6">
          <img src={PedestalIllustration} className="w-[300px] object-contain" />
          <div className="2xl:text-left text-center flex flex-col 2xl:items-start items-center justify-center">
            <p className="text-2xl text-gray-900 font-bold">No banking details found</p>
            <p className="text-gray-600 max-w-md">
              Connect your bank account to automatically pull statements and verify your income using{" "}
              <a
                className="text-green font-semibold"
                href="https://plaid.com"
                target="_blank"
                rel="noreferrer"
              >
                <PlaidLogo width={16} height={16} className="inline-block mb-1" /> Plaid
              </a>
            </p>
            <Button
              variant="green"
              className="mt-3 self-center 2xl:self-start"
              pendingWithIcon={pendingConnect}
              onClick={onClickConnect}
              disabled={pendingConnect}
            >
              Connect a bank account
              <LandmarkIcon height={16} width={16} className="ml-2" />
            </Button>
          </div>
        </div>
      ) : null}
      <div className="flex flex-col gap-y-6">
        <BankIncomeList incomes={incomes} applicationId={applicationId} loading={incomesLoading} />
        <BankAccountItemList bankAccounts={bankAccounts} loading={bankAccountsLoading} />
        <BankStatementList
          bankStatements={bankStatements}
          applicationId={applicationId}
          loading={bankStatementsLoading}
        />
      </div>
    </div>
  );
}

const BankStatementList = ({
  bankStatements,
  applicationId,
  loading,
}: { bankStatements?: entities.UploadedFile[]; applicationId?: string; loading?: boolean }) => {
  const { deleting, mutateAsync: deleteDocument } = useDeleteDocument({
    applicationId,
  });

  const previewDocument = usePreviewDocument();

  const confirmDeleteBankStatement = useConfirmBeforeRun({
    fn: deleteDocument,
    message: (
      <p>
        Are you sure you want to delete this bank statement? <br />
        <b>This action cannot be undone.</b>
      </p>
    ),
    loading: !!deleting,
  });

  if (loading) {
    return (
      <div className="flex flex-col gap-y-1">
        {[1, 2].map((i) => (
          <Skeleton height={60} key={i} />
        ))}
      </div>
    );
  }

  return (
    <div>
      {bankStatements?.length ? <p className="font-semibold text-2xl mb-2">Your bank statements</p> : null}
      <div>
        {bankStatements?.map((statement) => (
          <div
            className="flex flex-row hover:bg-gray-200 odd:bg-gray-100 p-2 odd:rounded gap-x-4"
            key={statement.id}
          >
            <div>
              <b className="text-lg">{statement.s3Document?.name || "Bank Statement"}</b>
              <p className="text-base text-gray-600">
                {statement.date ? formatters.date.formatDate(statement.date) : "No date"}
              </p>
            </div>
            <div className="flex flex-row gap-2 items-center ml-auto">
              {statement.s3Document?.href ? (
                <Button variant="outline" size="sm" onClick={() => previewDocument(statement)}>
                  View statement
                  <EyeIcon className="ml-2 w-3 h-3" />
                </Button>
              ) : null}
              <Button
                variant="destructive"
                size="sm"
                pendingWithIcon={deleting === statement.id}
                onClick={() => statement.id && confirmDeleteBankStatement(statement.id)}
              >
                Delete statement
                <TrashIcon className="ml-2 w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BankAccountItemList = ({
  bankAccounts,
  loading,
}: { bankAccounts?: entities.BankAccount[]; loading?: boolean }) => {
  const deleteBankAccountMutation = useDeleteBankAccount();

  const confirmDeleteBankAccount = useConfirmBeforeRun({
    fn: deleteBankAccountMutation.mutateAsync,
    message: (
      <p>
        Are you sure you want to delete this bank account? <br />
        <b>This action cannot be undone.</b>
      </p>
    ),
    loading: deleteBankAccountMutation.isPending,
  });

  if (loading) {
    return (
      <div className="flex flex-col gap-y-1">
        {[1, 2].map((i) => (
          <Skeleton height={60} key={i} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-row justify-between items-center gap-x-4 gap-y-2">
        {bankAccounts?.length ? (
          <p className="font-semibold text-2xl mb-2">Your bank and depository accounts</p>
        ) : null}
      </div>
      <div>
        {bankAccounts?.map((bankAccount) => (
          <div
            className="flex flex-row hover:bg-gray-200 odd:bg-gray-100 p-2 odd:rounded gap-x-4"
            key={bankAccount.id}
          >
            <div>
              <b className="text-lg">
                {bankAccount.institutionName} {bankAccount.name}
              </b>
              <p className="text-base text-gray-600">•••• •••• {bankAccount.accountNumberLast4Digits}</p>
            </div>
            <div>
              {bankAccount.currentBalance ? (
                <Badge variant="gray" className="text-xs rounded-full font-medium">
                  {formatters.number.getCurrencyFromNumber(bankAccount.currentBalance)}
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-row items-center ml-auto">
              <Button
                variant="destructive"
                size="sm"
                pendingWithIcon={deleteBankAccountMutation.deleting === bankAccount.id}
                onClick={() => confirmDeleteBankAccount(bankAccount.id)}
              >
                Delete account
                <TrashIcon className="ml-2 w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BankIncomeList = ({
  incomes,
  applicationId,
  loading,
}: { incomes?: entities.Income[]; applicationId?: string; loading?: boolean }) => {
  const deleteIncomeMutation = useDeleteIncomeMutation(applicationId);

  const confirmDeleteIncome = useConfirmBeforeRun({
    fn: deleteIncomeMutation.mutateAsync,
    message: (
      <p>
        Are you sure you want to delete this income source? <br />
        <b>This action cannot be undone.</b>
      </p>
    ),
    loading: deleteIncomeMutation.isPending,
  });

  if (loading) {
    return (
      <div className="flex flex-col gap-y-1">
        {[1, 2].map((i) => (
          <Skeleton height={60} key={i} />
        ))}
      </div>
    );
  }

  return (
    <div>
      {incomes?.length ? (
        <p className="font-semibold text-2xl mb-2">Your income sources from your bank statements</p>
      ) : null}
      <div>
        {incomes?.map((income) => (
          <div
            className="flex flex-row items-center hover:bg-gray-200 odd:bg-gray-100 p-2 odd:rounded gap-x-4"
            key={income.id}
          >
            <div className="flex-1">
              <div className="flex flex-row items-center gap-x-2">
                <div>
                  {income.employerName ? <b className="text-lg">{income.employerName}</b> : null}
                  {income.type ? (
                    <b className="text-lg">{formatters.enums.INCOME_TYPE_MAP[income.type]} income</b>
                  ) : null}
                </div>
                {income.payment?.amount ? (
                  <Badge variant="gray" className="text-xs rounded-full font-medium">
                    <p>
                      {formatters.number.getCurrencyFromNumber(income.payment.amount)}
                      {income.payment.frequency &&
                        ` ${formatters.enums.FREQUENCY_MAP[income.payment.frequency]?.toLowerCase()}`}
                    </p>

                    {income.payment.frequency !== enums.Frequency.ANNUAL ? (
                      <p className="ml-1">
                        (
                        {formatters.number.getCurrencyFromNumber(
                          calculators.payment.convertPaymentToAnnualAmount(income.payment)
                        )}{" "}
                        annually)
                      </p>
                    ) : null}
                  </Badge>
                ) : null}
              </div>
              {income.description ? <p className="text-sm">{income.description}</p> : null}
              <p className="text-base text-gray-600">
                {income.jobTitle}
                {income.employmentType && ` • ${income.employmentType}`}
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              pendingWithIcon={deleteIncomeMutation.deleting === income.id}
              onClick={() => income.id && confirmDeleteIncome(income.id)}
              disabled={deleteIncomeMutation.deleting === income.id}
            >
              Delete income
              <TrashIcon className="ml-2 w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
